import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { ShieldCheck } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/6221bdec-a1f4-4f2a-b3c7-5775bc29aacc/images/e31134d582097747674673484304c177d3bc9e13151123a0f77086a44dfae7a6.png";

export default function LoginPage() {
  const { login, loginMfa, error, setError } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // 2FA flow state
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res?.ok) return;
    if (res.mfa_required) {
      setMfaToken(res.mfa_token);
      return;
    }
    navigate("/diary");
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await loginMfa(mfaToken, mfaCode.trim());
    setBusy(false);
    if (res?.ok) navigate("/diary");
  };

  const cancelMfa = () => {
    setMfaToken("");
    setMfaCode("");
    setError("");
  };

  return (
    <main data-testid="login-page" className="min-h-[calc(100vh-90px)] grid grid-cols-1 lg:grid-cols-5">
      <div className="hidden lg:flex lg:col-span-2 relative border-r border-[#C1B49A] overflow-hidden">
        <div className="absolute inset-0 opacity-70" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "multiply" }} />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <p className="label-small text-[#8B3A3A] mb-4">{t("landing.quote_label")}</p>
          <p className="font-display text-3xl italic text-[#1A1918] leading-snug">{t("landing.quote")}</p>
        </div>
      </div>

      <div className="lg:col-span-3 flex items-center justify-center p-8 md:p-16">
        {!mfaToken ? (
          <form onSubmit={handleSubmit} className="w-full max-w-md anim-fade-up" data-testid="login-form">
            <p className="label-small mb-3">{t("login.eyebrow")}</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A1918] mb-2">{t("login.heading")}</h1>
            <p className="text-[#4A4238] mb-10">{t("login.sub")}</p>

            <div className="space-y-7">
              <div>
                <label className="label-small block mb-2">{t("login.label_email")}</label>
                <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-etched" autoComplete="email" />
              </div>
              <div>
                <label className="label-small block mb-2">{t("login.label_password")}</label>
                <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-etched" autoComplete="current-password" />
              </div>
              {error && (<p data-testid="login-error" className="text-sm text-[#8B3A3A] font-medium">{error}</p>)}
              <button data-testid="login-submit-btn" type="submit" disabled={busy} className="btn-primary w-full">
                {busy ? t("login.submitting") : t("login.submit")}
              </button>
              <p className="text-sm text-[#4A4238]">
                {t("login.no_account")}{" "}
                <Link to="/register" className="text-[#8B3A3A] underline underline-offset-4" data-testid="login-to-register-link">{t("login.register_link")}</Link>
                .
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleMfaSubmit} className="w-full max-w-md anim-fade-up" data-testid="login-mfa-form">
            <p className="label-small mb-3 flex items-center gap-2"><ShieldCheck size={12} /> {t("login.mfa_eyebrow")}</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A1918] mb-2">{t("login.mfa_heading")}</h1>
            <p className="text-[#4A4238] mb-8">{t("login.mfa_sub")}</p>

            <div className="space-y-7">
              <div>
                <label className="label-small block mb-2">{t("login.mfa_label")}</label>
                <input
                  data-testid="login-mfa-code-input"
                  type="text"
                  inputMode="text"
                  autoComplete="one-time-code"
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="input-etched font-mono tracking-[0.3em] text-lg"
                  placeholder="123 456"
                  autoFocus
                />
                <p className="text-xs italic text-[#7A7061] mt-2">{t("login.mfa_hint")}</p>
              </div>
              {error && (<p data-testid="login-mfa-error" className="text-sm text-[#8B3A3A] font-medium">{error}</p>)}
              <button data-testid="login-mfa-submit-btn" type="submit" disabled={busy || !mfaCode.trim()} className="btn-primary w-full">
                {busy ? t("login.submitting") : t("login.mfa_submit")}
              </button>
              <button
                data-testid="login-mfa-cancel-btn"
                type="button"
                onClick={cancelMfa}
                className="btn-ghost w-full text-sm"
              >
                {t("login.mfa_cancel")}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
